import React, { useState, useEffect } from 'react';
import { 
  CalendarIcon,
  PlusIcon,
  CakeIcon,
  GiftIcon,
  BuildingOfficeIcon,
  ListBulletIcon,
  CalendarDaysIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import calendarApi from '../../services/calendarApi';

const CompanyCalendar = () => {
  const [activeTab, setActiveTab] = useState('birthdays');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Data states
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [holidays, setHolidays] = useState([]);
  
  // Calendar navigation
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Modal states
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    holiday_date: '',
    description: ''
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchCalendarData();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setCurrentUser(data.user);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const [birthdaysData, anniversariesData, holidaysData] = await Promise.all([
        calendarApi.getBirthdays(),
        calendarApi.getAnniversaries(),
        calendarApi.getHolidays()
      ]);
      
      setBirthdays(birthdaysData.data.birthdays);
      setAnniversaries(anniversariesData.data.anniversaries);
      setHolidays(holidaysData.data.holidays);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHoliday = async () => {
    try {
      if (!holidayForm.name || !holidayForm.holiday_date) {
        toast.error('Name and date are required');
        return;
      }

      await calendarApi.createHoliday(holidayForm);
      toast.success('Holiday created successfully');
      setShowHolidayModal(false);
      setHolidayForm({ name: '', holiday_date: '', description: '' });
      fetchCalendarData();
    } catch (error) {
      console.error('Error creating holiday:', error);
      toast.error('Failed to create holiday');
    }
  };

  const handleUpdateHoliday = async () => {
    try {
      if (!holidayForm.name || !holidayForm.holiday_date) {
        toast.error('Name and date are required');
        return;
      }

      await calendarApi.updateHoliday(editingHoliday.id, holidayForm);
      toast.success('Holiday updated successfully');
      setShowHolidayModal(false);
      setEditingHoliday(null);
      setHolidayForm({ name: '', holiday_date: '', description: '' });
      fetchCalendarData();
    } catch (error) {
      console.error('Error updating holiday:', error);
      toast.error('Failed to update holiday');
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    if (!window.confirm(`Are you sure you want to delete "${holiday.name}"?`)) {
      return;
    }

    try {
      await calendarApi.deleteHoliday(holiday.id);
      toast.success('Holiday deleted successfully');
      fetchCalendarData();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      toast.error('Failed to delete holiday');
    }
  };

  const openEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      name: holiday.name,
      holiday_date: holiday.holiday_date,
      description: holiday.description || ''
    });
    setShowHolidayModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      // Handle the date string directly without creating a Date object
      // This completely avoids any timezone conversion issues
      const [year, month, day] = dateString.split('-');
      
      // Validate the components
      if (!year || !month || !day) {
        console.warn('Invalid date components:', { year, month, day });
        return 'Invalid date';
      }
      
      // Format manually using just the string components
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const monthIndex = parseInt(month) - 1;
      const dayNum = parseInt(day);
      
      if (monthIndex < 0 || monthIndex > 11 || dayNum < 1 || dayNum > 31) {
        console.warn('Invalid date values:', { monthIndex, dayNum });
        return 'Invalid date';
      }
      
      return `${monthNames[monthIndex]} ${dayNum}`;
    } catch (e) {
      console.warn('Error formatting date:', dateString, e);
      return 'Invalid date';
    }
  };

  const formatDateWithYear = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      // Handle the date string directly without creating a Date object
      // This completely avoids any timezone conversion issues
      const [year, month, day] = dateString.split('-');
      
      // Validate the components
      if (!year || !month || !day) {
        console.warn('Invalid date components:', { year, month, day });
        return 'Invalid date';
      }
      
      // Format manually using just the string components
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const monthIndex = parseInt(month) - 1;
      const dayNum = parseInt(day);
      
      if (monthIndex < 0 || monthIndex > 11 || dayNum < 1 || dayNum > 31) {
        console.warn('Invalid date values:', { monthIndex, dayNum });
        return 'Invalid date';
      }
      
      return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
    } catch (e) {
      console.warn('Error formatting date:', dateString, e);
      return 'Invalid date';
    }
  };

  const getMonthData = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDay = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return { days, firstDay, lastDay };
  };

  const getEventsForDate = (date) => {
    const events = [];
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    if (activeTab === 'birthdays') {
      birthdays.forEach(birthday => {
        if (birthday.birth_month === month && birthday.birth_day === day) {
          events.push({
            type: 'birthday',
            title: `${birthday.first_name} ${birthday.last_name}`,
            data: birthday
          });
        }
      });
    } else if (activeTab === 'anniversaries') {
      anniversaries.forEach(anniversary => {
        if (anniversary.hire_month === month && anniversary.hire_day === day) {
          events.push({
            type: 'anniversary',
            title: `${anniversary.first_name} ${anniversary.last_name} - ${anniversary.years_of_service} Year${anniversary.years_of_service !== 1 ? 's' : ''}`,
            data: anniversary
          });
        }
      });
    } else if (activeTab === 'holidays') {
      holidays.forEach(holiday => {
        // Parse the holiday date string directly to avoid timezone conversion
        const [year, month, day] = holiday.holiday_date.split('-');
        const holidayYear = parseInt(year);
        const holidayMonth = parseInt(month);
        const holidayDay = parseInt(day);
        
        if (holidayMonth === date.getMonth() + 1 && 
            holidayDay === date.getDate() &&
            holidayYear === date.getFullYear()) {
          events.push({
            type: 'holiday',
            title: holiday.name,
            data: holiday
          });
        }
      });
    }
    
    return events;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const renderListView = () => {
    let data = [];
    let emptyMessage = '';
    
    if (activeTab === 'birthdays') {
      data = birthdays;
      emptyMessage = 'No birthdays found';
    } else if (activeTab === 'anniversaries') {
      data = anniversaries;
      emptyMessage = 'No work anniversaries found';
    } else if (activeTab === 'holidays') {
      data = holidays;
      emptyMessage = 'No holidays found';
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">{emptyMessage}</div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                  activeTab === 'birthdays' ? 'bg-pink-100 text-pink-600' :
                  activeTab === 'anniversaries' ? 'bg-blue-100 text-blue-600' :
                  'bg-green-100 text-green-600'
                }`}>
                  {activeTab === 'birthdays' && <CakeIcon className="h-6 w-6" />}
                  {activeTab === 'anniversaries' && <GiftIcon className="h-6 w-6" />}
                  {activeTab === 'holidays' && <BuildingOfficeIcon className="h-6 w-6" />}
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {activeTab === 'birthdays' && `${item.first_name} ${item.last_name}`}
                    {activeTab === 'anniversaries' && `${item.first_name} ${item.last_name}`}
                    {activeTab === 'holidays' && item.name}
                  </h3>
                  
                  <p className="text-gray-600">
                    {activeTab === 'birthdays' && formatDate(item.birthday)}
                    {activeTab === 'anniversaries' && `${formatDate(item.hire_date)} - ${item.years_of_service} Year${item.years_of_service !== 1 ? 's' : ''}`}
                    {activeTab === 'holidays' && formatDateWithYear(item.holiday_date)}
                  </p>
                  
                  {activeTab === 'birthdays' && (
                    <p className="text-sm text-gray-500">{item.email}</p>
                  )}
                  {activeTab === 'anniversaries' && (
                    <p className="text-sm text-gray-500">{item.email}</p>
                  )}
                  {activeTab === 'holidays' && item.description && (
                    <p className="text-sm text-gray-500">{item.description}</p>
                  )}
                </div>
              </div>
              
              {activeTab === 'holidays' && currentUser?.role_type === 'admin' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => openEditHoliday(item)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteHoliday(item)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCalendarView = () => {
    const { days } = getMonthData();
    const today = new Date();
    
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="p-6">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              const events = getEventsForDate(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = day.toDateString() === today.toDateString();
              
              return (
                <div
                  key={index}
                  className={`min-h-[100px] p-2 border border-gray-100 ${
                    isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                  } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  } ${isToday ? 'text-blue-600' : ''}`}>
                    {day.getDate()}
                  </div>
                  
                  <div className="space-y-1">
                    {events.map((event, eventIndex) => (
                      <div
                        key={eventIndex}
                        className={`text-xs p-1 rounded truncate ${
                          event.type === 'birthday' ? 'bg-pink-100 text-pink-700' :
                          event.type === 'anniversary' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}
                        title={event.title}
                      >
                        {event.title}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'birthdays', name: 'Birthdays', icon: CakeIcon, color: 'pink' },
    { id: 'holidays', name: 'Holidays', icon: BuildingOfficeIcon, color: 'green' },
    { id: 'anniversaries', name: 'Work Anniversaries', icon: GiftIcon, color: 'blue' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <CalendarIcon className="h-8 w-8 mr-3 text-blue-600" />
                Company Calendar
              </h1>
              <p className="mt-2 text-gray-600">
                Keep track of birthdays, holidays, and work anniversaries
              </p>
            </div>
            
            {activeTab === 'holidays' && currentUser?.role_type === 'admin' && (
              <button
                onClick={() => {
                  setEditingHoliday(null);
                  setHolidayForm({ name: '', holiday_date: '', description: '' });
                  setShowHolidayModal(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Holiday
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? `border-${tab.color}-500 text-${tab.color}-600`
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="mb-6">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-lg flex items-center ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ListBulletIcon className="h-5 w-5 mr-2" />
              List View
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-lg flex items-center ${
                viewMode === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <CalendarDaysIcon className="h-5 w-5 mr-2" />
              Calendar View
            </button>
          </div>
        </div>

        {/* Content */}
        {viewMode === 'list' ? renderListView() : renderCalendarView()}

        {/* Holiday Modal */}
        {showHolidayModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Holiday Name
                  </label>
                  <input
                    type="text"
                    value={holidayForm.name}
                    onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Christmas Day"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={holidayForm.holiday_date}
                    onChange={(e) => setHolidayForm({ ...holidayForm, holiday_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={holidayForm.description}
                    onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Additional details about the holiday"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowHolidayModal(false);
                    setEditingHoliday(null);
                    setHolidayForm({ name: '', holiday_date: '', description: '' });
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingHoliday ? handleUpdateHoliday : handleCreateHoliday}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingHoliday ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyCalendar; 